import { CurrencyInput } from './../../../../../../../../../../react-common/components/currency-input/CurrencyInput';
import Dropdown from './../../../../../../../../../../react-common/components/dropdown/Dropdown';
import * as React from 'react';
import { useState } from 'react';
import { ToggleButton } from '@app/react-common/components/toggle-button/ToggleButton';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import Input from '@app/react-common/components/input/Input';
import { FallbackRequest } from '../../../models/FallbackRequest';
import { RateType } from '../../../constants/RateType';

const RATE_TYPE_OPTIONS = [
  { label: 'Fixed', value: RateType.FIXED },
  { label: 'Percentage', value: RateType.PERCENTAGE },
];

interface SetFallbackRateModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  onClose: () => void;
  onCreateSuccess: (request: FallbackRequest[]) => void;
  fallbackMethods: FallbackRequest[]; // Array for both domestic and international
  currencySign: string;
}

interface FallbackRateFormData {
  id?: number;
  name: string;
  rate: number;
  rateType: RateType;
  isInternational?: boolean;
  disablePOBoxCheck?: boolean;
  enabled?: boolean;
}

export const SetFallbackRateModal: React.FC<SetFallbackRateModalProps> = (props: SetFallbackRateModalProps) => {
  const [formData, setFormData] = useState<{
    domestic: FallbackRateFormData;
    international: FallbackRateFormData;
  }>(() => {
    const defaultDomestic = {
      id: undefined,
      name: 'Standard Shipping',
      rate: 0,
      rateType: RateType.FIXED,
      isInternational: false,
      disablePOBoxCheck: false,
      enabled: false,
    };

    const defaultInternational = {
      id: undefined,
      name: 'Standard Shipping',
      rate: 0,
      rateType: RateType.FIXED,
      isInternational: true,
      disablePOBoxCheck: false,
      enabled: false,
    };

    const foundDomestic = props.fallbackMethods.find((x) => !x.isInternational);
    const foundInternational = props.fallbackMethods.find((x) => x.isInternational);

    return {
      domestic:
        props.mode === 'create' && foundDomestic
          ? { ...defaultDomestic, id: foundDomestic.id } // Use default values but keep the id
          : foundDomestic
            ? { ...foundDomestic, rateType: foundDomestic.rateType || RateType.FIXED }
            : defaultDomestic,
      international:
        props.mode === 'create' && foundInternational
          ? { ...defaultInternational, id: foundInternational.id } // Use default values but keep the id
          : foundInternational
            ? { ...foundInternational, rateType: foundInternational.rateType || RateType.FIXED }
            : defaultInternational,
    };
  });

  const [formValid, setFormValid] = useState<boolean>(props.mode === 'edit' ? true : false);

  const handleInputChange = (type: 'domestic' | 'international', field: keyof FallbackRateFormData, value: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [type]: { ...prevData[type], [field]: value },
    }));

    const updatedDomesticRate = type === 'domestic' && field === 'rate' ? value : formData.domestic.rate;
    const updatedInternationalRate = type === 'international' && field === 'rate' ? value : formData.international.rate;

    setFormValid(props.mode === 'create' && updatedDomesticRate === 0 && updatedInternationalRate === 0 ? false : true);
  };

  const handleSave = () => {
    console.log('domestic', formData.domestic.rate, formData.domestic.rateType);
    console.log('international', formData.international.rate, formData.international.rateType);
    props.onCreateSuccess([{ ...formData.domestic }, { ...formData.international }]);
  };

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <div className="set-fallback-rate-modal">
        <h2 className="set-fallback-rate-modal__title">
          {props.mode === 'edit' ? 'Edit fallback rate' : 'Turn on fallback rate?'}
        </h2>
        <div className="set-fallback-rate-modal__content">
          <p>
            Once turned on, the fallback rate will only be available to orders where none of your delivery methods
            apply.
          </p>

          {/* Domestic Fallback Rate Section */}
          <div className="toggle">
            <h4>For domestic deliveries</h4>
            <div className="toggle__toggle-switch">
              {
                <ToggleButton
                  checked={formData.domestic.enabled}
                  onChange={(e) => handleInputChange('domestic', 'enabled', e)}
                />
              }
            </div>
          </div>
          <div className="row">
            <div className="row__rate-name">
              <label>Rate name</label>
              <Input
                type="text"
                name="domesticRateName"
                value={formData.domestic.name}
                onChange={(e) => handleInputChange('domestic', 'name', e.target.value)}
                placeholder="Rate name"
              />
            </div>
            <div className="row__rate-value">
              <div className="rate-input-group">
                <div className="currency-input-wrapper">
                  <label>Rate Per Delivery</label>
                  <CurrencyInput
                    name="domesticRate"
                    value={formData.domestic.rate}
                    setValue={(value: number) => handleInputChange('domestic', 'rate', value)}
                    setValid={(valid) => setFormValid(valid)}
                    currencySign={formData.domestic.rateType === RateType.PERCENTAGE ? '%' : props.currencySign}
                    minValue={0.0}
                  />
                </div>
                <Dropdown
                  options={RATE_TYPE_OPTIONS}
                  preSelectedOption={RATE_TYPE_OPTIONS.find((option) => option.value === formData.domestic.rateType)}
                  onSelect={(option) => {
                    if (option) {
                      handleInputChange('domestic', 'rateType', option.value);
                    }
                  }}
                  placeholder="Select type"
                  width={120}
                />
              </div>
            </div>
          </div>
          <div className="set-fallback-rate-modal__checkbox">
            <input
              type="checkbox"
              checked={formData.domestic.disablePOBoxCheck}
              onChange={(e) => handleInputChange('domestic', 'disablePOBoxCheck', e.target.checked)}
            />
            <label>Don’t use fallback if shipping address is a PO box</label>
          </div>

          {/* International Fallback Rate Section */}
          <div className="toggle">
            <h4>For international deliveries</h4>
            <div className="toggle__toggle-switch">
              {
                <ToggleButton
                  checked={formData.international.enabled}
                  onChange={(e) => handleInputChange('international', 'enabled', e)}
                />
              }
            </div>
          </div>
          <div className="row">
            <div className="row__rate-name">
              <label>Rate name</label>
              <Input
                type="text"
                name="internationalRateName"
                value={formData.international.name}
                onChange={(e) => handleInputChange('international', 'name', e.target.value)}
                placeholder="Rate name"
              />
            </div>
            <div className="row__rate-value">
              <div className="rate-input-group">
                <div className="currency-input-wrapper">
                  <label>Rate Per Delivery</label>
                  <CurrencyInput
                    name="internationalRate"
                    value={formData.international.rate}
                    setValue={(value: number) => handleInputChange('international', 'rate', value)}
                    setValid={(valid) => setFormValid(valid)}
                    currencySign={formData.international.rateType === RateType.PERCENTAGE ? '%' : props.currencySign}
                    minValue={0.0}
                  />
                </div>
                <Dropdown
                  options={RATE_TYPE_OPTIONS}
                  preSelectedOption={RATE_TYPE_OPTIONS.find(
                    (option) => option.value === formData.international.rateType,
                  )}
                  onSelect={(option) => {
                    if (option) {
                      handleInputChange('international', 'rateType', option.value);
                    }
                  }}
                  placeholder="Select type"
                  width={120}
                />
              </div>
            </div>
          </div>
          <div className="set-fallback-rate-modal__checkbox">
            <input
              type="checkbox"
              checked={formData.international.disablePOBoxCheck}
              onChange={(e) => handleInputChange('international', 'disablePOBoxCheck', e.target.checked)}
            />
            <label>Don’t use fallback if shipping address is a PO box</label>
          </div>
        </div>

        <div className="set-fallback-rate-modal__actions">
          <Button
            onClick={props.onClose}
            text="Cancel"
            size="medium"
            variant="secondary"
            className="set-fallback-rate-modal__actions__cancel"
          />
          <Button
            disabled={!formValid}
            onClick={handleSave}
            text={props.mode === 'edit' ? 'Save' : 'Turn on'}
            size="medium"
            variant="primary"
          />
        </div>
      </div>
    </Modal>
  );
};
